import { forwardRef } from "react";
import { CustomBank, Fill } from "@educaplay/core/components";
import BankContent from "./components/BankContent";
import GameGrid from "./components/GameGrid";
import { selectorCheck } from "./utils/selectorCheck";
import { useSelector } from "react-redux";

import {
  useGameState,
  useKeyboardControls,
  usePointerControls,
  useGridLayout,
  useWordSelection,
  useGameAnimations
} from "./hooks";
import { useTranslate } from "@educaplay/core";

/**
 * Componente principal del juego de sopa de letras - Versión final refactorizada
 *
 * @param {Object} props - Propiedades del componente
 * @param {Function} props.onAnswer - Callback cuando se responde
 * @param {Object} ref - Referencia del componente
 */
const Game = forwardRef(function Game({ onAnswer }, ref) {
  const gameState = useGameState();
  const t = useTranslate();
  const hasBank = useSelector((state) => state.game.hasBank);

  const { updateGridSize } = useGridLayout({
    gridRef: gameState.gridRef,
    tileRefs: gameState.tileRefs,
    setUserAnswers: gameState.setUserAnswers
  });

  const wordSelection = useWordSelection({
    currentQuestion: gameState.currentQuestion,
    currentQuestionIndex: gameState.currentQuestionIndex,
    questions: gameState.questions,
    words: gameState.words,
    tiles: gameState.tiles,
    gridSize: gameState.gridSize,
    tileRefs: gameState.tileRefs,
    time: gameState.time,
    hasFinished: gameState.hasFinished,
    penalties: gameState.penalties,
    userAnswers: gameState.userAnswers,
    setUserAnswers: gameState.setUserAnswers,
    foundWordsArray: gameState.foundWordsArray,
    setFoundWordsArray: gameState.setFoundWordsArray,
    setActiveSelector: gameState.setActiveSelector,
    setGridShown: gameState.setGridShown,
    gridRef: gameState.gridRef,
    onAnswer
  });
  const keyboardControls = useKeyboardControls({
    tiles: gameState.tiles,
    gridSize: gameState.gridSize,
    pointerUpHandler: wordSelection.pointerUpHandler,
    tileRefs: gameState.tileRefs,
    chainToSelector: wordSelection.chainToSelector,
    selectorCheck,
    words: gameState.words
  });

  const pointerControls = usePointerControls({
    tiles: gameState.tiles,
    gridSize: gameState.gridSize,
    gridRef: gameState.gridRef,
    tileRefs: gameState.tileRefs,
    activeSelector: gameState.activeSelector,
    setActiveSelector: gameState.setActiveSelector
  });

  useGameAnimations({
    ref,
    currentQuestionIndex: gameState.currentQuestionIndex,
    questions: gameState.questions,
    words: gameState.words,
    userAnswers: gameState.userAnswers,
    setGridShown: gameState.setGridShown,
    setFoundWordsArray: gameState.setFoundWordsArray,
    updateGridSize,
    setUserAnswers: gameState.setUserAnswers
  });

  const handleTileUp = () => {
    wordSelection.handleActivePointerUp(gameState.activeSelector);
  };

  const handleTileDown = (x, y) => {
    if (keyboardControls.navigationMode || keyboardControls.keyboardChain.length > 0) {
      keyboardControls.cancelKeyboardHandler();
    }
    pointerControls.pointerDownHandler(x, y);
  };
  return (
    <>
      {hasBank && (
        <CustomBank>
          <BankContent
            data={gameState.words}
            dashedArray={gameState.foundWordsArray}
          />
        </CustomBank>
      )}
      <Fill name={"core-bank-right"}>
        {t("specific.screen")}: {gameState.currentQuestionIndex + 1} / {gameState.questions.length}
      </Fill>
      <Fill name={"core-bank-left"}>
        {t("specific.words")}: {gameState.foundWordsArray.filter((word) => !!word).length} / {gameState.words.length}
      </Fill>

      <GameGrid
        tiles={gameState.tiles}
        gridSize={gameState.gridSize}
        gridShown={gameState.gridShown}
        currentQuestionIndex={gameState.currentQuestionIndex}
        gridRef={gameState.gridRef}
        tileRefs={gameState.tileRefs}
        activeSelector={gameState.activeSelector}
        userAnswers={gameState.userAnswers}
        keyboardChain={keyboardControls.keyboardChain}
        navigationMode={keyboardControls.navigationMode}
        onTileDown={handleTileDown}
        onTileUp={handleTileUp}
        onPointerMove={pointerControls.pointerMoveHandler}
        onPointerLeave={pointerControls.pointerLeaveHandler}
      />
    </>
  );
});

export default Game;
